import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CreditCard,
  User,
  Calendar,
  Check,
  X,
  Edit,
  Trash2,
  ArrowRight,
  AlertCircle,
  Clock,
  CheckCircle,
  FileText,
  DollarSign,
  Building2,
  ShieldAlert
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getLoan,
  approveLoan,
  rejectLoan,
  forwardLoan,
  cancelLoan,
  updateLoan,
  deleteLoan
} from '../../api/payroll';
import { useAuth } from '../../contexts/AuthContext';
import BlurredValue from '../../components/common/BlurredValue';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const LoanDetailPage = ({ module = 'retail' }) => {
  const { loanId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const roleLevels = {
    staff: 1,
    branch_staff: 1,
    branch_admin: 2,
    admin: 3,
    wholesale_admin: 3,
    retail_admin: 3,
    system_admin: 4,
    super_admin: 5
  };

  const userLevel = roleLevels[user?.role] || 1;

  const [loan, setLoan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Modals state
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Form state for Grant / Approve
  const [approveData, setApproveData] = useState({
    repayment_method: 'one_time',
    installments_count: '1',
    repayment_period: 'one_time_payroll_run',
    deduction_sequence: '1',
    deduction_type: 'automatic',
    deduction_reminder: false
  });

  // Form state for Reject
  const [rejectionNotes, setRejectionNotes] = useState('');

  // Form state for Edit
  const [editFormData, setEditFormData] = useState({
    loan_type: 'personal',
    principal_amount: '',
    monthly_deduction: '',
    description: '',
    start_date: '',
    notes: ''
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const fetchLoanData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getLoan(loanId);
      setLoan(data);
      setEditFormData({
        loan_type: data.loan_type || 'personal',
        principal_amount: data.principal_amount || '',
        monthly_deduction: data.monthly_deduction || '',
        description: data.description || '',
        start_date: data.start_date ? data.start_date.substring(0, 10) : '',
        notes: data.notes || ''
      });
    } catch (error) {
      toast.error('Failed to load loan details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [loanId]);

  useEffect(() => {
    fetchLoanData();
  }, [fetchLoanData]);

  if (loading) {
    return <LoadingSpinner className="min-h-screen" />;
  }

  if (!loan) {
    return (
      <div className="min-h-screen bg-secondary-offWhite p-6 flex flex-col items-center justify-center">
        <AlertCircle size={48} className="text-red-500 mb-4" />
        <h2 className="text-xl font-bold text-gray-800">Loan Request Not Found</h2>
        <button
          onClick={() =>
            navigate(module === 'wholesale' ? '/wholesale/payroll/loans' : '/payroll/loans')
          }
          className="mt-4 px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg"
        >
          Return to Loans
        </button>
      </div>
    );
  }

  const requesterLevel = roleLevels[loan.requester_role] || 1;
  const isPersonalRequest = loan.user_id === user.id || loan.created_by === user.id;
  const isCurrentlyAssigned = loan.assigned_to_id === user.id;
  const isAuthorizedReviewer = isCurrentlyAssigned || userLevel >= 4;

  const canUserGrant =
    userLevel >= 4 || (userLevel >= 3 && !isPersonalRequest && requesterLevel < userLevel);

  const backLink = module === 'wholesale' ? '/wholesale/payroll/loans' : '/payroll/loans';

  // Handlers
  const handleApproveSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await approveLoan(loan.id, approveData);
      toast.success('Loan request approved and granted successfully');
      setShowApproveModal(false);
      fetchLoanData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve loan');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejectSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await rejectLoan(loan.id, { notes: rejectionNotes });
      toast.success('Loan request rejected');
      setShowRejectModal(false);
      fetchLoanData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reject loan');
    } finally {
      setSubmitting(false);
    }
  };

  const handleForwardSubmit = async () => {
    if (!confirm('Forward this loan request to the next level assignee?')) return;
    setSubmitting(true);
    try {
      await forwardLoan(loan.id);
      toast.success('Loan request forwarded successfully');
      fetchLoanData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to forward loan');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelSubmit = async () => {
    if (!confirm('Are you sure you want to cancel this pending loan request?')) return;
    setSubmitting(true);
    try {
      await cancelLoan(loan.id);
      toast.success('Loan request cancelled');
      fetchLoanData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to cancel loan');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await updateLoan(loan.id, editFormData);
      toast.success('Loan request updated successfully');
      setShowEditModal(false);
      fetchLoanData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update loan');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSubmit = async () => {
    if (deleteConfirmText.trim() !== 'DELETE') return;
    setSubmitting(true);
    try {
      await deleteLoan(loan.id);
      toast.success('Loan deleted successfully');
      navigate(backLink);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete loan');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      active: 'bg-green-100 text-green-800 border-green-200',
      completed: 'bg-blue-100 text-blue-800 border-blue-200',
      rejected: 'bg-red-100 text-red-800 border-red-200',
      cancelled: 'bg-gray-100 text-gray-700 border-gray-200'
    };
    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-bold uppercase border ${styles[status] || 'bg-gray-100'}`}
      >
        {status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-secondary-offWhite p-3 sm:p-6">
      <div className="max-w-6xl mx-auto space-y-6 pb-12">
        {/* Navigation & Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(backLink)}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
              title="Back to Loans"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl sm:text-2xl font-bold text-primary-black">
                  Loan Details #{loan.id}
                </h1>
                {getStatusBadge(loan.status)}
              </div>
              <p className="text-sm text-gray-500 mt-1 capitalize">
                {loan.loan_type?.replace('_', ' ')} Loan Request
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Reviewer / Admin Grant & Reject */}
            {loan.status === 'pending' && isAuthorizedReviewer && (
              <>
                {canUserGrant && (
                  <button
                    onClick={() => setShowApproveModal(true)}
                    className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white font-semibold text-sm rounded-lg hover:bg-emerald-700 transition"
                  >
                    <Check size={16} /> Grant Loan
                  </button>
                )}
                <button
                  onClick={() => setShowRejectModal(true)}
                  className="flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white font-semibold text-sm rounded-lg hover:bg-red-700 transition"
                >
                  <X size={16} /> Reject
                </button>
                {userLevel < 4 && (
                  <button
                    onClick={handleForwardSubmit}
                    disabled={submitting}
                    className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white font-semibold text-sm rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                  >
                    <ArrowRight size={16} /> Forward
                  </button>
                )}
              </>
            )}

            {/* Owner Edit & Cancel */}
            {loan.status === 'pending' && (isPersonalRequest || userLevel >= 4) && (
              <>
                <button
                  onClick={() => setShowEditModal(true)}
                  className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 text-gray-700 bg-white font-semibold text-sm rounded-lg hover:bg-gray-50 transition"
                >
                  <Edit size={16} /> Edit
                </button>
                {isPersonalRequest && (
                  <button
                    onClick={handleCancelSubmit}
                    disabled={submitting}
                    className="flex items-center gap-1.5 px-4 py-2 border border-red-300 text-red-600 bg-white font-semibold text-sm rounded-lg hover:bg-red-50 transition disabled:opacity-50"
                  >
                    <X size={16} /> Cancel
                  </button>
                )}
              </>
            )}

            {/* Admin Delete */}
            {userLevel >= 4 && (
              <button
                onClick={() => setShowDeleteModal(true)}
                className="flex items-center gap-1.5 px-3 py-2 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 border border-transparent rounded-lg transition font-semibold"
                title="Delete Loan Record"
              >
                <Trash2 size={16} /> Delete
              </button>
            )}
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Details (2 Cols) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Financial Overview Card */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100 space-y-4">
              <h3 className="text-lg font-semibold text-primary-black border-b pb-3 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-accent-gold" /> Financial Summary
              </h3>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 font-medium">Principal Amount</p>
                  <p className="text-xl font-bold text-primary-black mt-1">
                    <BlurredValue value={formatCurrency(loan.principal_amount)} />
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 font-medium">Current Balance</p>
                  <p className="text-xl font-bold text-blue-600 mt-1">
                    <BlurredValue value={formatCurrency(loan.balance)} />
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 font-medium">Monthly Deduction</p>
                  <p className="text-xl font-bold text-emerald-600 mt-1">
                    <BlurredValue value={formatCurrency(loan.monthly_deduction)} />
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 font-medium">Repayment Method</p>
                  <p className="text-sm font-bold text-primary-black mt-1 capitalize">
                    {loan.repayment_method?.replace('_', ' ') || 'One Time'}
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 font-medium">Installments Count</p>
                  <p className="text-sm font-bold text-primary-black mt-1">
                    {loan.installments_count || 1} period(s)
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 font-medium">Start Date</p>
                  <p className="text-sm font-bold text-primary-black mt-1">
                    {loan.start_date ? new Date(loan.start_date).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>

              {loan.description && (
                <div className="pt-2">
                  <p className="text-xs text-gray-500 font-medium">Reason / Description</p>
                  <p className="text-sm text-gray-800 mt-1 bg-gray-50 p-3 rounded-lg border border-gray-100">
                    {loan.description}
                  </p>
                </div>
              )}

              {loan.notes && (
                <div className="pt-1">
                  <p className="text-xs text-gray-500 font-medium">Review Notes / Comments</p>
                  <p className="text-sm text-gray-700 mt-1 bg-amber-50/50 p-3 rounded-lg border border-amber-100">
                    {loan.notes}
                  </p>
                </div>
              )}
            </div>

            {/* Repayment History Log */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100 space-y-4">
              <h3 className="text-lg font-semibold text-primary-black border-b pb-3 flex items-center gap-2">
                <FileText className="w-5 h-5 text-accent-gold" /> Repayment History
              </h3>

              {loan.repayments && loan.repayments.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                      <tr>
                        <th className="p-3">Date</th>
                        <th className="p-3">Deduction Type</th>
                        <th className="p-3 text-right">Amount Paid</th>
                        <th className="p-3 text-right">Remaining Balance</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {loan.repayments.map((rep) => (
                        <tr key={rep.id} className="hover:bg-gray-50">
                          <td className="p-3 text-gray-700">
                            {new Date(rep.payment_date || rep.created_at).toLocaleDateString()}
                          </td>
                          <td className="p-3 text-gray-800">
                            {rep.run_description
                              ? `Payroll: ${rep.run_description}`
                              : rep.payroll_run_id
                                ? `Payroll Run #${rep.payroll_run_id}`
                                : 'Manual Repayment'}
                          </td>
                          <td className="p-3 text-right font-semibold text-emerald-600">
                            <BlurredValue value={formatCurrency(rep.amount)} />
                          </td>
                          <td className="p-3 text-right font-semibold text-gray-700">
                            <BlurredValue value={formatCurrency(rep.remaining_balance)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-6">
                  No repayment transactions recorded yet.
                </p>
              )}
            </div>
          </div>

          {/* Sidebar Audit Info (1 Col) */}
          <div className="space-y-6">
            {/* Requester Info */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100 space-y-3">
              <h3 className="text-base font-semibold text-primary-black border-b pb-2 flex items-center gap-2">
                <User className="w-4 h-4 text-accent-gold" /> Employee Information
              </h3>
              <div>
                <p className="text-xs text-gray-400">Full Name</p>
                <p className="text-sm font-semibold text-primary-black">
                  {loan.full_name || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Email</p>
                <p className="text-sm text-gray-700">{loan.email || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Role / Level</p>
                <p className="text-sm text-gray-700 capitalize">
                  {loan.requester_role?.replace('_', ' ')} (Level {requesterLevel})
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Branch</p>
                <p className="text-sm text-gray-700">{loan.branch_name || 'All Branches'}</p>
              </div>
            </div>

            {/* Workflow & Audit Trail */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100 space-y-3">
              <h3 className="text-base font-semibold text-primary-black border-b pb-2 flex items-center gap-2">
                <Clock className="w-4 h-4 text-accent-gold" /> Audit & Review Trail
              </h3>
              <div>
                <p className="text-xs text-gray-400">Requested On</p>
                <p className="text-sm text-gray-800">
                  {loan.created_at ? new Date(loan.created_at).toLocaleString() : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Assigned Reviewer</p>
                <p className="text-sm font-medium text-gray-800">
                  {loan.assigned_to_name || 'Unassigned'}
                </p>
              </div>
              {loan.forwarded_by_name && (
                <div>
                  <p className="text-xs text-gray-400">Forwarded By</p>
                  <p className="text-sm text-gray-800">{loan.forwarded_by_name}</p>
                </div>
              )}
              {loan.approved_by_name && (
                <div>
                  <p className="text-xs text-gray-400">Approved By</p>
                  <p className="text-sm font-semibold text-emerald-700">
                    {loan.approved_by_name} (
                    {loan.approved_at ? new Date(loan.approved_at).toLocaleDateString() : ''})
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Grant & Approve Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-bold text-primary-black flex items-center gap-2">
              <CheckCircle className="text-emerald-600" /> Grant & Approve Loan
            </h3>

            <form onSubmit={handleApproveSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Repayment Method
                </label>
                <select
                  value={approveData.repayment_method}
                  onChange={(e) =>
                    setApproveData({ ...approveData, repayment_method: e.target.value })
                  }
                  className="w-full p-2.5 border rounded-lg text-sm bg-white"
                >
                  <option value="one_time">One-Time Deduction</option>
                  <option value="installments">Monthly Installments</option>
                </select>
              </div>

              {approveData.repayment_method === 'installments' && (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Number of Periods / Installments
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={approveData.installments_count}
                    onChange={(e) =>
                      setApproveData({ ...approveData, installments_count: e.target.value })
                    }
                    className="w-full p-2.5 border rounded-lg text-sm"
                    required
                  />
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowApproveModal(false)}
                  className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-emerald-600 text-white font-semibold text-sm rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                >
                  Confirm Grant
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-bold text-red-600 flex items-center gap-2">
              <AlertCircle /> Reject Loan Request
            </h3>
            <form onSubmit={handleRejectSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Reason for Rejection (Optional)
                </label>
                <textarea
                  value={rejectionNotes}
                  onChange={(e) => setRejectionNotes(e.target.value)}
                  rows="3"
                  className="w-full p-2.5 border rounded-lg text-sm"
                  placeholder="Enter rejection notes..."
                ></textarea>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowRejectModal(false)}
                  className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-red-600 text-white font-semibold text-sm rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  Reject Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-bold text-primary-black flex items-center gap-2">
              <Edit className="text-accent-gold" /> Edit Loan Request
            </h3>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Loan Type</label>
                <select
                  value={editFormData.loan_type}
                  onChange={(e) => setEditFormData({ ...editFormData, loan_type: e.target.value })}
                  className="w-full p-2.5 border rounded-lg text-sm bg-white"
                >
                  <option value="personal">Personal Loan</option>
                  <option value="salary_advance">Salary Advance</option>
                  <option value="emergency">Emergency Loan</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Principal Amount (NGN)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={editFormData.principal_amount}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, principal_amount: e.target.value })
                  }
                  className="w-full p-2.5 border rounded-lg text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Monthly Deduction (NGN)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={editFormData.monthly_deduction}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, monthly_deduction: e.target.value })
                  }
                  className="w-full p-2.5 border rounded-lg text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Description / Reason
                </label>
                <textarea
                  value={editFormData.description}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, description: e.target.value })
                  }
                  rows="2"
                  className="w-full p-2.5 border rounded-lg text-sm"
                ></textarea>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-accent-gold text-primary-black font-semibold text-sm rounded-lg hover:bg-accent-goldDark disabled:opacity-50"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-bold text-red-600 flex items-center gap-2">
              <ShieldAlert /> Confirm Delete Loan
            </h3>
            <p className="text-sm text-gray-600">
              This will permanently delete this loan record (Loan #{loan.id}). Type{' '}
              <strong className="text-red-600">DELETE</strong> to confirm.
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type DELETE"
              className="w-full p-2.5 border rounded-lg text-sm"
            />
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteSubmit}
                disabled={submitting || deleteConfirmText.trim() !== 'DELETE'}
                className="px-4 py-2 bg-red-600 text-white font-semibold text-sm rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoanDetailPage;
